import { Routes } from '@angular/router';
import { ShellComponent } from './shell/shell.component';
import { authGuard } from './auth/auth.guard';

export const routes: Routes = [
  // Focus Mode is a sibling of the shell, not a child. The cockpit owns the entire
  // viewport — no nav, no sidebar, no escape but the four buttons.
  {
    path: 'focus/:id',
    canActivate: [authGuard],
    loadComponent: () => import('./pages/focus/focus.component').then(m => m.FocusComponent),
  },
  // The capture surface — what the app IS when it launches (see the '' redirect
  // below). A shell sibling like Focus: one bar, no nav, the full Command Center
  // a click away. Chad's primary use: dump thoughts; auto-triage plans them.
  {
    path: 'capture',
    canActivate: [authGuard],
    loadComponent: () => import('./pages/capture/capture.component').then(m => m.CaptureComponent),
  },
  {
    path: '',
    component: ShellComponent,
    canActivate: [authGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: '/capture' },
      {
        path: 'plan',
        loadComponent: () => import('./pages/plan/plan.component').then(m => m.PlanComponent),
      },
      {
        path: 'triage',
        loadComponent: () => import('./pages/triage/triage.component').then(m => m.TriageComponent),
      },
      {
        path: 'triage/:id',
        loadComponent: () => import('./pages/triage/triage.component').then(m => m.TriageComponent),
      },
      {
        // Sales Desk (slice 3) — the desk merged INTO Today (note + accept/dismiss
        // recommendations on /plan; its chat tools ride the COS bar). The route
        // stays as a redirect so muscle memory and old links keep working.
        path: 'sales-desk',
        redirectTo: 'plan',
      },
      // The pages below are reachable but intentionally not in the primary nav. They
      // exist for inspection, not for the daily directive surface.
      {
        path: 'actions',
        loadComponent: () => import('./pages/actions/actions.component').then(m => m.ActionsComponent),
      },
      {
        path: 'prospects',
        loadComponent: () => import('./pages/prospects-list/prospects-list.component').then(m => m.ProspectsListComponent),
      },
      {
        // Prospects AI — daily brief (read-only). Must precede 'prospects/:id'
        // so "brief" isn't matched as a prospect id.
        path: 'prospects/brief',
        loadComponent: () => import('./pages/prospects-brief/prospects-brief.component').then(m => m.ProspectsBriefComponent),
      },
      {
        // Phase 33+ — screenshot ingest. Same "before /:id" reason as brief above.
        path: 'prospects/ingest',
        loadComponent: () => import('./pages/prospect-ingest/prospect-ingest.component').then(m => m.ProspectIngestComponent),
      },
      {
        // Phase 27 — opportunity kanban backed by WealthBox snapshots.
        // Lives in the primary nav alongside Prospects since opportunities
        // are the deal-stage view of the same book.
        path: 'pipeline',
        loadComponent: () => import('./pages/pipeline/pipeline.component').then(m => m.PipelineComponent),
      },
      {
        path: 'prospects/:id',
        loadComponent: () => import('./pages/prospect-detail/prospect-detail.component').then(m => m.ProspectDetailComponent),
      },
      // Universal Subject detail. Used when navigating from places that don't know
      // the kind in advance (daily plan, search). Prospect-kind subjects redirect
      // to /prospects/:id from inside the component so deep links keep working.
      // Per design: this page is intentionally bare — name + open actions +
      // "Open in Wealthbox". The CRM owns full contact / activity / tag detail.
      {
        path: 'subjects/:id',
        loadComponent: () => import('./pages/subject-detail/subject-detail.component').then(m => m.SubjectDetailComponent),
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
      },
      {
        path: 'agent',
        loadComponent: () => import('./pages/agent-chat/agent-chat.component').then(m => m.AgentChatComponent),
      },
      {
        path: 'notebooks',
        loadComponent: () => import('./pages/notebooks/notebooks.component').then(m => m.NotebooksComponent),
      },
      {
        path: 'notebooks/:slug',
        loadComponent: () => import('./pages/notebooks/notebooks.component').then(m => m.NotebooksComponent),
      },
      {
        path: 'insights',
        loadComponent: () => import('./pages/insights/insights.component').then(m => m.InsightsComponent),
      },
      {
        path: 'settings',
        loadComponent: () => import('./pages/settings/settings.component').then(m => m.SettingsComponent),
      },
      {
        path: 'profile',
        loadComponent: () => import('./pages/profile/profile.component').then(m => m.ProfileComponent),
      },
      // ===== Phase 39 Research =====
      {
        path: 'research',
        loadComponent: () => import('./pages/research-dashboard/research-dashboard.component')
          .then(m => m.ResearchDashboardComponent),
      },
      {
        path: 'research/risk',
        loadComponent: () => import('./pages/research-risk/research-risk.component')
          .then(m => m.ResearchRiskComponent),
      },
      {
        path: 'research/demographics',
        loadComponent: () => import('./pages/research-demographics/research-demographics.component')
          .then(m => m.ResearchDemographicsComponent),
      },
      {
        path: 'research/macro',
        loadComponent: () => import('./pages/research-macro/research-macro.component')
          .then(m => m.ResearchMacroComponent),
      },
      {
        // Phase 40.6 — Short Sale & Market Structure (FINRA daily short-sale volume)
        path: 'research/short-sale',
        loadComponent: () => import('./pages/research-short-sale/research-short-sale.component')
          .then(m => m.ResearchShortSaleComponent),
      },
      {
        // Phase 40 — Research Analyst (grounded macro/geopolitical chat + charts)
        path: 'research/analyst',
        loadComponent: () => import('./pages/research-analyst/research-analyst.component')
          .then(m => m.ResearchAnalystComponent),
      },
      // ===== Phase 42 Securities Research =====
      {
        // Dossiers (the ADV 8.A checklist from EDGAR) + falsifier-required theses + watchlist
        path: 'research/securities',
        loadComponent: () => import('./pages/research-securities/research-securities.component')
          .then(m => m.ResearchSecuritiesComponent),
      },
      {
        // Model holdings + risk analytics (concentration, drawdown, stress, country-risk join)
        path: 'research/portfolio',
        loadComponent: () => import('./pages/research-portfolio/research-portfolio.component')
          .then(m => m.ResearchPortfolioComponent),
      },
      {
        // Purchasing-power Monte Carlo over a model book
        path: 'research/montecarlo',
        loadComponent: () => import('./pages/research-montecarlo/research-montecarlo.component')
          .then(m => m.ResearchMontecarloComponent),
      },
      // ===== Phase 26 Compliance =====
      {
        path: 'compliance',
        loadComponent: () => import('./pages/compliance-dashboard/compliance-dashboard.component')
          .then(m => m.ComplianceDashboardComponent),
      },
      {
        path: 'compliance/marketing',
        loadComponent: () => import('./pages/compliance-marketing/compliance-marketing-list.component')
          .then(m => m.ComplianceMarketingListComponent),
      },
      {
        path: 'compliance/marketing/new',
        loadComponent: () => import('./pages/compliance-marketing/compliance-marketing-upload.component')
          .then(m => m.ComplianceMarketingUploadComponent),
      },
      {
        path: 'compliance/marketing/reviews/:reviewId',
        loadComponent: () => import('./pages/compliance-marketing/compliance-marketing-review.component')
          .then(m => m.ComplianceMarketingReviewComponent),
      },
      {
        path: 'compliance/code-of-ethics/request-trade',
        loadComponent: () => import('./pages/code-of-ethics/request-trade.component')
          .then(m => m.RequestTradeComponent),
      },
      {
        path: 'compliance/code-of-ethics/restricted-list',
        loadComponent: () => import('./pages/code-of-ethics/restricted-list.component')
          .then(m => m.RestrictedListComponent),
      },
      {
        path: 'compliance/attestations',
        loadComponent: () => import('./pages/compliance-attestations/attestations-list.component')
          .then(m => m.AttestationsListComponent),
      },
      {
        path: 'compliance/attestations/:id',
        loadComponent: () => import('./pages/compliance-attestations/attestations-sign.component')
          .then(m => m.AttestationsSignComponent),
      },
      {
        path: 'compliance/disclosures',
        loadComponent: () => import('./pages/compliance-disclosures/disclosures.component')
          .then(m => m.DisclosuresComponent),
      },
      {
        path: 'compliance/vendors',
        loadComponent: () => import('./pages/compliance-vendors/vendors-list.component')
          .then(m => m.VendorsListComponent),
      },
      {
        path: 'compliance/vendors/:id',
        loadComponent: () => import('./pages/compliance-vendors/vendor-detail.component')
          .then(m => m.VendorDetailComponent),
      },
      {
        path: 'compliance/documents',
        loadComponent: () => import('./pages/compliance-documents/documents.component')
          .then(m => m.ComplianceDocumentsComponent),
      },
      {
        path: 'compliance/annual-review',
        loadComponent: () => import('./pages/compliance-annual-review/annual-review-list.component')
          .then(m => m.AnnualReviewListComponent),
      },
      {
        path: 'compliance/annual-review/:id',
        loadComponent: () => import('./pages/compliance-annual-review/annual-review-detail.component')
          .then(m => m.AnnualReviewDetailComponent),
      },
      {
        path: 'compliance/regulatory',
        loadComponent: () => import('./pages/compliance-regulatory/regulatory.component')
          .then(m => m.RegulatoryComponent),
      },
      {
        path: 'compliance/reports',
        loadComponent: () => import('./pages/compliance-reports/reports.component')
          .then(m => m.ComplianceReportsComponent),
      },
      {
        path: 'compliance/setup',
        loadComponent: () => import('./pages/compliance-setup/compliance-setup.component')
          .then(m => m.ComplianceSetupComponent),
      },
      {
        // Phase 36 — Website Compliance Archive (RFC 3161 + WORM blob).
        path: 'compliance/website-archive',
        loadComponent: () => import('./pages/compliance-website-archive/website-archive.component')
          .then(m => m.WebsiteArchiveComponent),
      },
    ],
  },
];
